/**
 * RulesPage - Rules Engine with Variables and Operational Rules
 * PROMPT 49: Configurable operational, welfare, and compliance rules
 * EF-TASK-TYPE-REFORM-03: Updated to use ID-based category/subcategory selectors
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Scale,
  Pencil,
  Trash2,
  Plus,
  Settings2,
  Shield,
  AlertTriangle,
  CheckCircle,
  Clock,
  Users,
  Coffee,
  Moon,
  RefreshCw,
  Loader2,
  Info,
  ChevronRight,
  Link as LinkIcon,
  Utensils,
  UserCheck,
  Plane,
  Save,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import { cn } from "../lib/utils";
import { LoadingSpinner, EmptyState } from "../components/shared/DataComponents";
import api, { extractErrorMessage, getTaskCategories, getTaskSubCategories } from "../lib/api";
import { usePlatformSettings } from "../components/settings/PlatformSettingsContext";

// Category icons and colors
const CATEGORY_CONFIG = {
  welfare: { icon: Coffee, color: "text-green-400", bg: "bg-green-500/10", label: "Welfare" },
  operations: { icon: Users, color: "text-blue-400", bg: "bg-blue-500/10", label: "Operations" },
  compliance: { icon: Shield, color: "text-purple-400", bg: "bg-purple-500/10", label: "Compliance" },
  scheduling: { icon: Clock, color: "text-yellow-400", bg: "bg-yellow-500/10", label: "Scheduling" },
};

// Rule type badges
const RULE_TYPE_CONFIG = {
  validation: { color: "bg-red-500/20 text-red-400 border-red-500/30", label: "Validation", icon: Shield },
  warning: { color: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30", label: "Warning", icon: AlertTriangle },
  auto_calculate: { color: "bg-blue-500/20 text-blue-400 border-blue-500/30", label: "Auto Calculate", icon: RefreshCw },
};

// Breach policy configuration
const BREACH_POLICY_CONFIG = {
  forbidden: { 
    color: "bg-red-600/20 text-red-400 border-red-600/30", 
    label: "Forbidden", 
    icon: Shield,
    description: "Cannot be breached under any circumstances"
  },
  allow_with_confirmation: { 
    color: "bg-amber-500/20 text-amber-400 border-amber-500/30", 
    label: "Confirm", 
    icon: AlertTriangle,
    description: "Can be breached with explicit confirmation"
  },
  allow_with_reason: { 
    color: "bg-blue-500/20 text-blue-400 border-blue-500/30", 
    label: "Justify", 
    icon: Info,
    description: "Requires written justification to breach"
  },
};

// Trigger labels
const TRIGGER_LABELS = {
  shift_create: "Shift Create",
  shift_edit: "Shift Edit",
  assignment_create: "Assignment Create",
  assignment_edit: "Assignment Edit",
};

export default function RulesPage() {
  const { getCurrencySymbol } = usePlatformSettings();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("variables");
  
  // Rule Variables state
  const [variables, setVariables] = useState([]);
  const [variablesLoading, setVariablesLoading] = useState(true);
  const [editingVariable, setEditingVariable] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [savingVariable, setSavingVariable] = useState(false);
  
  // Rules state
  const [rules, setRules] = useState([]);
  const [rulesLoading, setRulesLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRule, setSelectedRule] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    rule_type: "validation",
    trigger: "shift_create",
    prompt_text: "",
    is_active: true,
    priority: 0,
    breach_policy: "forbidden",
  });

  // EF-RULE-DEFINITIONS-01: Task Rule Associations state
  const [taskAssociations, setTaskAssociations] = useState([]);
  const [taskAssociationsLoading, setTaskAssociationsLoading] = useState(true);
  const [taskDefinitions, setTaskDefinitions] = useState([]);
  const [savingAssociation, setSavingAssociation] = useState(null);
  
  // EF-TASK-TYPE-REFORM-04: Categories state for ID-based selection (no legacy fields)
  const [taskCategories, setTaskCategories] = useState([]);
  const [taskSubCategories, setTaskSubCategories] = useState([]);
  
  // EF-TASK-TYPE-REFORM-04: Get filtered subcategories based on selected category
  const getFilteredSubCategories = useCallback((categoryId) => {
    if (!categoryId) return [];
    return taskSubCategories.filter(sc => sc.category_id === categoryId);
  }, [taskSubCategories]);
  
  // EF-TASK-TYPE-REFORM-04: Count matching tasks using ID-based filtering
  const getMatchingTasksCount = useCallback((categoryId, subcategoryId) => {
    return taskDefinitions.filter(td => 
      td.category_id === categoryId && 
      td.subcategory_id === subcategoryId &&
      td.is_active
    ).length;
  }, [taskDefinitions]);

  // Load rule variables
  const loadVariables = useCallback(async () => {
    setVariablesLoading(true);
    try {
      const response = await api.get("/rule-variables");
      setVariables(response.data);
    } catch (err) {
      toast.error(`Failed to load variables: ${extractErrorMessage(err)}`);
    } finally {
      setVariablesLoading(false);
    }
  }, []);

  // Load rules
  const loadRules = useCallback(async () => {
    setRulesLoading(true);
    try {
      const response = await api.get("/rules");
      setRules(response.data);
    } catch (err) {
      toast.error(`Failed to load rules: ${extractErrorMessage(err)}`);
    } finally {
      setRulesLoading(false);
    }
  }, []);

  // EF-RULE-DEFINITIONS-01: Load task associations
  const loadTaskAssociations = useCallback(async () => {
    setTaskAssociationsLoading(true);
    try {
      const response = await api.get("/rule-task-associations");
      setTaskAssociations(response.data);
    } catch (err) {
      toast.error(`Failed to load task associations: ${extractErrorMessage(err)}`);
    } finally {
      setTaskAssociationsLoading(false);
    }
  }, []);

  // Load task definitions for dropdown options
  const loadTaskDefinitions = useCallback(async () => {
    try {
      const response = await api.get("/task-definitions", { params: { limit: 500 } });
      setTaskDefinitions(response.data);
    } catch (err) {
      console.error("Failed to load task definitions:", err);
    }
  }, []);
  
  // EF-TASK-TYPE-REFORM-03: Load task categories and subcategories
  const loadTaskCategories = useCallback(async () => {
    try {
      const [catRes, subCatRes] = await Promise.all([
        getTaskCategories(),
        getTaskSubCategories(),
      ]);
      setTaskCategories(catRes.data);
      setTaskSubCategories(subCatRes.data);
    } catch (err) {
      console.error("Failed to load task categories:", err);
    }
  }, []);

  useEffect(() => {
    loadVariables();
    loadRules();
    loadTaskAssociations();
    loadTaskDefinitions();
    loadTaskCategories();
  }, [loadVariables, loadRules, loadTaskAssociations, loadTaskDefinitions, loadTaskCategories]);

  // EF-RULE-DEFINITIONS-01: Update task association
  const handleUpdateAssociation = async (ruleKey, updates) => {
    setSavingAssociation(ruleKey);
    try {
      await api.patch(`/rule-task-associations/${ruleKey}`, updates);
      toast.success(`${ruleKey} association updated`);
      loadTaskAssociations();
    } catch (err) {
      toast.error(`Failed to update: ${extractErrorMessage(err)}`);
    } finally {
      setSavingAssociation(null);
    }
  };

  // Update a rule variable
  const handleUpdateVariable = async (key) => {
    setSavingVariable(true);
    try {
      await api.patch(`/rule-variables/${key}`, { value: editValue });
      toast.success("Variable updated");
      setEditingVariable(null);
      loadVariables();
    } catch (err) {
      toast.error(`Failed to update: ${extractErrorMessage(err)}`);
    } finally {
      setSavingVariable(false);
    }
  };

  // Start editing a variable
  const startEditingVariable = (variable) => {
    setEditingVariable(variable.key);
    setEditValue(variable.value);
  };

  // Cancel editing
  const cancelEditingVariable = () => {
    setEditingVariable(null);
    setEditValue("");
  };

  // Seed default rules
  const handleSeedRules = async () => {
    try {
      const response = await api.post("/rules/seed-defaults");
      toast.success(response.data.message);
      loadRules();
    } catch (err) {
      toast.error(`Failed to seed rules: ${extractErrorMessage(err)}`);
    }
  };

  // Rule CRUD operations
  const handleCreateRule = async () => {
    try {
      await api.post("/rules", formData);
      toast.success("Rule created");
      setDialogOpen(false);
      loadRules();
      resetForm();
    } catch (err) {
      toast.error(`Failed to create rule: ${extractErrorMessage(err)}`);
    }
  };

  const handleUpdateRule = async () => {
    if (!selectedRule) return;
    try {
      await api.patch(`/rules/${selectedRule.id}`, formData);
      toast.success("Rule updated");
      setDialogOpen(false);
      loadRules();
      resetForm();
    } catch (err) {
      toast.error(`Failed to update rule: ${extractErrorMessage(err)}`);
    }
  };

  const handleDeleteRule = async () => {
    if (!selectedRule) return;
    try {
      await api.delete(`/rules/${selectedRule.id}`);
      toast.success("Rule deleted");
      setDeleteDialogOpen(false);
      setSelectedRule(null);
      loadRules();
    } catch (err) {
      toast.error(`Failed to delete rule: ${extractErrorMessage(err)}`);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      rule_type: "validation",
      trigger: "shift_create",
      prompt_text: "",
      is_active: true,
      priority: 0,
      breach_policy: "forbidden",
    });
    setSelectedRule(null);
  };

  const openEditDialog = (rule) => {
    setSelectedRule(rule);
    setFormData({
      name: rule.name,
      description: rule.description,
      rule_type: rule.rule_type,
      trigger: rule.trigger,
      prompt_text: rule.prompt_text,
      is_active: rule.is_active,
      priority: rule.priority || 0,
      breach_policy: rule.breach_policy || "forbidden",
    });
    setDialogOpen(true);
  };

  const openDeleteDialog = (rule) => {
    setSelectedRule(rule);
    setDeleteDialogOpen(true);
  };

  // Format variable value for display
  const formatVariableValue = (variable) => {
    if (variable.value_type === "time") {
      return variable.value;
    }
    if (variable.unit === "currency") {
      return `${getCurrencySymbol()}${variable.value.toFixed(2)}`;
    }
    if (variable.unit) {
      return `${variable.value} ${variable.unit}`;
    }
    return variable.value;
  };

  // Group variables by category
  // EVENTSFLO-RULES-PAGE-REPLACE-MEAL-AMOUNT-WITH-TASK-SUPPLEMENT-REFERENCE-V1
  // Hide the legacy `meal_allowance_amount` rule variable from the UI.
  // Audit (EVENTSFLO-MEAL-ALLOWANCE-PRICE-SOURCE-AUDIT-V1) confirmed it has
  // ZERO active call sites — the live Meal Allowance price is sourced from
  // `task_definitions["Meal Allowance"].default_charge_rate`. We replace
  // the misleading editable row with a read-only Task Definition reference
  // (see the welfare card's footer row below). The DB document is kept for
  // backward compatibility; only the UI hides it.
  const HIDDEN_VARIABLE_KEYS = new Set(["meal_allowance_amount"]);
  const variablesByCategory = variables
    .filter((v) => !HIDDEN_VARIABLE_KEYS.has(v.key))
    .reduce((acc, v) => {
      const cat = v.category || "operations";
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(v);
      return acc;
    }, {});

  // EVENTSFLO-RULES-PAGE-REPLACE-MEAL-AMOUNT-WITH-TASK-SUPPLEMENT-REFERENCE-V1
  // The Meal Allowance Task Definition is the actual price source consumed
  // by `evaluate_rules_for_shift` → `create_shift_task_line_item`. Pull it
  // straight from the already-loaded `taskDefinitions` state (no extra
  // API call needed) and show it as a read-only reference inside the
  // Welfare category.
  const mealAllowanceTaskDef = useMemo(() => {
    const list = Array.isArray(taskDefinitions) ? taskDefinitions : [];
    // Prefer exact "Meal Allowance" match (matches the regex the backend
    // rule uses: ^Meal Allowance$). Fall back to supplement_group="meal"
    // primary entry if the canonical name is renamed.
    const exact = list.find(
      (td) =>
        td &&
        (td.name || "").trim().toLowerCase() === "meal allowance" &&
        td.is_active !== false
    );
    if (exact) return exact;
    return list.find(
      (td) =>
        td &&
        td.supplement_group === "meal" &&
        td.task_type === "supplement" &&
        td.is_active !== false
    );
  }, [taskDefinitions]);

  return (
    <div className="space-y-6" data-testid="rules-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Scale className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Rule Definitions</h1>
            <p className="text-sm text-muted-foreground">
              Configure operational, welfare, and compliance rules
            </p>
          </div>
        </div>
      </div>

      {/* EVENTSFLO-SETTINGS-RULES-LABEL-ACTIVE-VS-AI-REVIEW-ONLY-V1
         ACTIVE banner — these variables are read by the live backend rule
         engine on every shift create / edit / copy / assignment. */}
      <Card
        className="border-emerald-500/40 bg-emerald-500/5"
        data-testid="rules-page-active-banner"
      >
        <CardContent className="py-4 flex items-start gap-3">
          <CheckCircle className="h-5 w-5 text-emerald-500 shrink-0 mt-0.5" />
          <div className="text-sm leading-relaxed">
            <div className="font-semibold text-emerald-700 dark:text-emerald-300 mb-1">
              ACTIVE — these values are used by the live backend rule engine
            </div>
            <p className="text-foreground/90">
              Thresholds live here (Crew Chief, Meal Allowance, Seats per Car,
              etc.). <strong>Prices and rates</strong> live in{" "}
              <strong>Task Definitions / Supplements</strong>. The AI Rules page
              is review-only and does not affect saved shifts.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3 lg:w-auto lg:inline-grid">
          <TabsTrigger value="variables" className="gap-2" data-testid="tab-variables">
            <Settings2 className="h-4 w-4" />
            Rule Variables
          </TabsTrigger>
          <TabsTrigger value="rules" className="gap-2" data-testid="tab-rules">
            <Shield className="h-4 w-4" />
            Rules ({rules.length})
          </TabsTrigger>
          <TabsTrigger value="task-associations" className="gap-2" data-testid="tab-task-associations">
            <LinkIcon className="h-4 w-4" />
            Task Associations
          </TabsTrigger>
        </TabsList>

        {/* ============== RULE VARIABLES TAB ============== */}
        <TabsContent value="variables" className="space-y-6">
          {/* Info Banner */}
          <Card className="bg-blue-500/10 border-blue-500/30">
            <CardContent className="p-4 flex items-start gap-3">
              <Info className="h-5 w-5 text-blue-400 mt-0.5" />
              <div>
                <p className="text-sm text-blue-300">
                  Rule variables are configurable values used by multiple rules. 
                  Changes take effect immediately for future calculations.
                </p>
              </div>
            </CardContent>
          </Card>

          {variablesLoading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner />
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(CATEGORY_CONFIG).map(([category, config]) => {
                const categoryVars = variablesByCategory[category] || [];
                if (categoryVars.length === 0) return null;
                
                const Icon = config.icon;
                
                return (
                  <Card key={category}>
                    <CardHeader className="pb-3">
                      <CardTitle className={cn("flex items-center gap-2 text-lg", config.color)}>
                        <Icon className="h-5 w-5" />
                        {config.label}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {categoryVars.map((variable) => {
                          const isEditing = editingVariable === variable.key;
                          
                          return (
                            <div
                              key={variable.key}
                              className={cn(
                                "flex items-center justify-between p-4 rounded-lg border transition-colors",
                                isEditing ? "bg-primary/5 border-primary/30" : "bg-white/5 border-white/10"
                              )}
                              data-testid={`variable-${variable.key}`}
                            >
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <h4 className="font-medium">{variable.name}</h4>
                                  {variable.unit && (
                                    <Badge variant="outline" className="text-xs">
                                      {variable.unit}
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-sm text-muted-foreground mt-1">
                                  {variable.description}
                                </p>
                              </div>
                              
                              <div className="flex items-center gap-3 ml-4">
                                {isEditing ? (
                                  <>
                                    <Input
                                      value={editValue}
                                      onChange={(e) => setEditValue(e.target.value)}
                                      className="w-32 h-9"
                                      autoFocus
                                      type={variable.value_type === "number" ? "number" : "text"}
                                      min={variable.min_value}
                                      max={variable.max_value}
                                      data-testid={`variable-input-${variable.key}`}
                                    />
                                    <Button
                                      size="sm"
                                      onClick={() => handleUpdateVariable(variable.key)}
                                      disabled={savingVariable}
                                    >
                                      {savingVariable ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                      ) : (
                                        <CheckCircle className="h-4 w-4" />
                                      )}
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={cancelEditingVariable}
                                    >
                                      Cancel
                                    </Button>
                                  </>
                                ) : (
                                  <>
                                    <span className={cn(
                                      "text-xl font-bold min-w-[80px] text-right",
                                      config.color
                                    )}>
                                      {formatVariableValue(variable)}
                                    </span>
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <Button
                                            size="sm"
                                            variant="ghost"
                                            onClick={() => startEditingVariable(variable)}
                                            data-testid={`edit-variable-${variable.key}`}
                                          >
                                            <Pencil className="h-4 w-4" />
                                          </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>Edit value</TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  </>
                                )}
                              </div>
                            </div>
                          );
                        })}

                        {/* EVENTSFLO-RULES-PAGE-REPLACE-MEAL-AMOUNT-WITH-TASK-SUPPLEMENT-REFERENCE-V1
                           Welfare-only read-only reference card replacing the
                           legacy `meal_allowance_amount` variable. The live
                           Meal Allowance rule sources its price from this
                           Task Definition; this row makes that source explicit
                           and offers a one-click jump to edit it. */}
                        {category === "welfare" && (
                          <div
                            className="flex items-start justify-between gap-4 p-4 rounded-lg border border-amber-500/30 bg-amber-500/5"
                            data-testid="meal-allowance-supplement-reference"
                          >
                            <div className="flex items-start gap-3 flex-1">
                              <Utensils className="h-5 w-5 text-amber-500 mt-0.5 shrink-0" />
                              <div>
                                <div className="flex items-center gap-2 flex-wrap">
                                  <h4 className="font-medium">Meal Allowance Supplement</h4>
                                  <Badge
                                    variant="outline"
                                    className="text-xs bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/40"
                                  >
                                    ACTIVE PRICE SOURCE
                                  </Badge>
                                </div>
                                <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                                  The live Meal Allowance rule adds the selected
                                  Task Definition / Supplement. The price is
                                  taken from that Task Definition, <strong>not</strong>{" "}
                                  from Rule Variables.
                                </p>
                                {mealAllowanceTaskDef ? (
                                  <div className="mt-3 text-sm space-y-1">
                                    <div>
                                      <span className="text-muted-foreground">Task Definition:</span>{" "}
                                      <span className="font-medium">
                                        {mealAllowanceTaskDef.name}
                                      </span>
                                    </div>
                                    <div>
                                      <span className="text-muted-foreground">Default charge rate:</span>{" "}
                                      <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                                        {getCurrencySymbol?.() || "€"}
                                        {Number(
                                          mealAllowanceTaskDef.default_charge_rate ?? 0
                                        ).toFixed(2)}
                                      </span>
                                      <span className="text-muted-foreground"> per crew member</span>
                                    </div>
                                    {mealAllowanceTaskDef.supplement_group && (
                                      <div className="text-xs text-muted-foreground">
                                        supplement_group: <code>{mealAllowanceTaskDef.supplement_group}</code>
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <div className="mt-3 text-sm text-amber-700 dark:text-amber-300">
                                    No active Meal Allowance Task Definition found.
                                    Create one in Task Definitions / Supplements so the
                                    rule can attach a priced line item.
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="shrink-0">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => navigate("/task-definitions")}
                                data-testid="edit-meal-allowance-supplement-btn"
                              >
                                Edit Meal Allowance Supplement
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* ============== RULES TAB ============== */}
        <TabsContent value="rules" className="space-y-4">
          {/* Actions */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {rules.length === 0 && (
                <Button variant="outline" onClick={handleSeedRules}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Seed Default Rules
                </Button>
              )}
            </div>
            <Button onClick={() => { resetForm(); setDialogOpen(true); }} data-testid="add-rule-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Rule
            </Button>
          </div>

          {/* Rules Table */}
          {rulesLoading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner />
            </div>
          ) : rules.length === 0 ? (
            <EmptyState
              icon={Scale}
              title="No Rules Configured"
              description="Add operational rules or seed the default rules to get started."
            />
          ) : (
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Rule</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Breach Policy</TableHead>
                    <TableHead>Trigger</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rules.map((rule) => {
                    const typeConfig = RULE_TYPE_CONFIG[rule.rule_type] || RULE_TYPE_CONFIG.validation;
                    const TypeIcon = typeConfig.icon;
                    const breachConfig = BREACH_POLICY_CONFIG[rule.breach_policy] || BREACH_POLICY_CONFIG.forbidden;
                    const BreachIcon = breachConfig.icon;
                    
                    return (
                      <TableRow key={rule.id} data-testid={`rule-row-${rule.id}`}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{rule.name}</p>
                            <p className="text-sm text-muted-foreground line-clamp-1">
                              {rule.description}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={cn("gap-1", typeConfig.color)}>
                            <TypeIcon className="h-3 w-3" />
                            {typeConfig.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Badge className={cn("gap-1 cursor-help", breachConfig.color)}>
                                  <BreachIcon className="h-3 w-3" />
                                  {breachConfig.label}
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="max-w-xs">{breachConfig.description}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm">
                            {TRIGGER_LABELS[rule.trigger] || rule.trigger}
                          </span>
                        </TableCell>
                        <TableCell>
                          {rule.is_active ? (
                            <Badge className="bg-green-500/20 text-green-400">Active</Badge>
                          ) : (
                            <Badge variant="secondary">Inactive</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => openEditDialog(rule)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => openDeleteDialog(rule)}
                            >
                              <Trash2 className="h-4 w-4 text-red-400" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Card>
          )}
        </TabsContent>

        {/* ============== TASK ASSOCIATIONS TAB (EF-RULE-DEFINITIONS-01) ============== */}
        <TabsContent value="task-associations" className="space-y-6">
          {/* Info Banner */}
          <Card className="bg-purple-500/10 border-purple-500/30">
            <CardContent className="p-4 flex items-start gap-3">
              <LinkIcon className="h-5 w-5 text-purple-400 mt-0.5 shrink-0" />
              <div>
                <p className="font-medium text-purple-400">Task Rule Associations</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Configure which task definitions appear when rules trigger during shift creation.
                  This controls the dropdown options shown to operators when rules fire.
                </p>
              </div>
            </CardContent>
          </Card>

          {taskAssociationsLoading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner />
            </div>
          ) : (
            <div className="space-y-4">
              {taskAssociations.map((assoc) => {
                const isSaving = savingAssociation === assoc.rule_key;
                const RuleIcon = assoc.rule_key === "MEAL" ? Utensils 
                  : assoc.rule_key === "CREW_CHIEF" ? UserCheck 
                  : Plane;
                const ruleColor = assoc.rule_key === "MEAL" ? "text-amber-500" 
                  : assoc.rule_key === "CREW_CHIEF" ? "text-teal-500" 
                  : "text-purple-500";
                const ruleBg = assoc.rule_key === "MEAL" ? "bg-amber-500/10 border-amber-500/30" 
                  : assoc.rule_key === "CREW_CHIEF" ? "bg-teal-500/10 border-teal-500/30" 
                  : "bg-purple-500/10 border-purple-500/30";
                
                return (
                  <Card key={assoc.rule_key} className={cn("border", ruleBg)}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        {/* Rule Info */}
                        <div className="flex items-center gap-3">
                          <RuleIcon className={cn("h-6 w-6", ruleColor)} />
                          <div>
                            <h3 className="font-semibold">{assoc.rule_key.replace("_", " ")}</h3>
                            <p className="text-xs text-muted-foreground">
                              {assoc.rule_key === "MEAL" && "Triggers when shift > 5 hours"}
                              {assoc.rule_key === "CREW_CHIEF" && "Triggers when crew >= 4"}
                              {assoc.rule_key === "TRAVEL" && "Triggers when Out of Town is enabled"}
                            </p>
                          </div>
                        </div>

                        {/* Active Toggle */}
                        <div className="flex items-center gap-2">
                          <Label className="text-xs text-muted-foreground">Active</Label>
                          <Switch
                            checked={assoc.is_active}
                            onCheckedChange={(checked) => handleUpdateAssociation(assoc.rule_key, { is_active: checked })}
                            disabled={isSaving}
                            data-testid={`association-active-${assoc.rule_key}`}
                          />
                        </div>
                      </div>

                      {/* Configuration Fields - EF-TASK-TYPE-REFORM-03: ID-based cascading dropdowns */}
                      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                        {/* Task Category (ID-based) */}
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">Task Category</Label>
                          <Select
                            value={assoc.category_id || ""}
                            onValueChange={(v) => handleUpdateAssociation(assoc.rule_key, { 
                              category_id: v,
                              subcategory_id: "", // Reset subcategory when category changes
                            })}
                            disabled={isSaving}
                          >
                            <SelectTrigger className="h-9" data-testid={`association-category-${assoc.rule_key}`}>
                              <SelectValue placeholder="Select..." />
                            </SelectTrigger>
                            <SelectContent>
                              {taskCategories.map((cat) => (
                                <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Task SubCategory (ID-based, filtered by category) */}
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">SubCategory</Label>
                          <Select
                            value={assoc.subcategory_id || ""}
                            onValueChange={(v) => handleUpdateAssociation(assoc.rule_key, { subcategory_id: v })}
                            disabled={isSaving || !assoc.category_id}
                          >
                            <SelectTrigger className="h-9" data-testid={`association-subcategory-${assoc.rule_key}`}>
                              <SelectValue placeholder={!assoc.category_id ? "Select category first" : "Select..."} />
                            </SelectTrigger>
                            <SelectContent>
                              {getFilteredSubCategories(assoc.category_id).map((subCat) => (
                                <SelectItem key={subCat.id} value={subCat.id}>{subCat.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Allow Multiple */}
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">Allow Multiple</Label>
                          <div className="flex items-center gap-2 h-9">
                            <Switch
                              checked={assoc.allow_multiple}
                              onCheckedChange={(checked) => handleUpdateAssociation(assoc.rule_key, { allow_multiple: checked })}
                              disabled={isSaving}
                              data-testid={`association-allow-multiple-${assoc.rule_key}`}
                            />
                            <span className="text-sm text-muted-foreground">
                              {assoc.allow_multiple ? "Multi-select" : "Single"}
                            </span>
                          </div>
                        </div>

                        {/* Matching Tasks Preview (ID-based count) */}
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">Matching Tasks</Label>
                          <div className="h-9 flex items-center gap-2">
                            <Badge variant="outline" className="text-xs">
                              {getMatchingTasksCount(assoc.category_id, assoc.subcategory_id)} active
                            </Badge>
                          </div>
                        </div>
                      </div>

                      {/* Saving Indicator */}
                      {isSaving && (
                        <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
                          <Loader2 className="h-3 w-3 animate-spin" />
                          Saving...
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}

              {taskAssociations.length === 0 && (
                <Card className="border-dashed">
                  <CardContent className="p-8 text-center">
                    <LinkIcon className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-50" />
                    <p className="text-muted-foreground">No task associations configured</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Task associations will be auto-seeded on server startup
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Rule Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {selectedRule ? "Edit Rule" : "Add New Rule"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Rule name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="What does this rule check?"
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type</Label>
                <Select
                  value={formData.rule_type}
                  onValueChange={(v) => setFormData({ ...formData, rule_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="validation">Validation (Blocks)</SelectItem>
                    <SelectItem value="warning">Warning (Acknowledge)</SelectItem>
                    <SelectItem value="auto_calculate">Auto Calculate</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Trigger</Label>
                <Select
                  value={formData.trigger}
                  onValueChange={(v) => setFormData({ ...formData, trigger: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="shift_create">Shift Create</SelectItem>
                    <SelectItem value="shift_edit">Shift Edit</SelectItem>
                    <SelectItem value="assignment_create">Assignment Create</SelectItem>
                    <SelectItem value="assignment_edit">Assignment Edit</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Breach Policy *</Label>
              <Select
                value={formData.breach_policy}
                onValueChange={(v) => setFormData({ ...formData, breach_policy: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="forbidden">
                    <div className="flex items-center gap-2">
                      <Shield className="h-4 w-4 text-red-400" />
                      <span>Forbidden - Cannot be breached</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="allow_with_confirmation">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-amber-400" />
                      <span>Confirm - Requires explicit confirmation</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="allow_with_reason">
                    <div className="flex items-center gap-2">
                      <Info className="h-4 w-4 text-blue-400" />
                      <span>Justify - Requires written reason</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {BREACH_POLICY_CONFIG[formData.breach_policy]?.description}
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="prompt_text">Prompt Text *</Label>
              <Textarea
                id="prompt_text"
                value={formData.prompt_text}
                onChange={(e) => setFormData({ ...formData, prompt_text: e.target.value })}
                placeholder="Message shown to user when rule triggers"
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="priority">Priority (lower = higher priority)</Label>
                <Input
                  id="priority"
                  type="number"
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })}
                  min={0}
                  max={99}
                />
              </div>
              <div className="flex items-center gap-3 pt-6">
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(v) => setFormData({ ...formData, is_active: v })}
                />
                <Label>Active</Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={selectedRule ? handleUpdateRule : handleCreateRule}>
              {selectedRule ? "Save Changes" : "Create Rule"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Rule</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedRule?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteRule} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
